import { AttachmentImage } from "@/components/ui/attachment-media";
import type { CardPreviewProps } from "@/components/shapes/card-previews";
import { TintBlock } from "@/components/shapes/shared/card-visual-parts";
import { Icons } from "@/components/ui/icons";
import { cn } from "@/lib/cn";
import { frameDisplayTitle } from "@/lib/frame-display-title";
import { rememberImageSize } from "@/lib/image-natural-size";
import { firstCardImage } from "./image-card-content";

/** Before an image's size is known a card shows it at 4:3. */
const DEFAULT_ASPECT = 4 / 3;
/**
 * An image of unknown size holds the 4:3 box while it loads (the wall's
 * estimate), then takes its own aspect.
 */
const UNKNOWN_ASPECT = "auto 4 / 3";

/**
 * An image frame: its first image edge to edge at its natural aspect. An
 * image the card cannot carry (inline bytes) shows a soft placeholder in the
 * frame's tint with its description, never a bare title.
 */
export function ImageCardPreview({ frame, source, density }: CardPreviewProps) {
  const image = firstCardImage(source.images);
  const label = image?.alt || frameDisplayTitle(frame);
  const compact = density === "compact";
  if (image?.url) {
    return (
      <AttachmentImage
        src={image.url}
        alt={label}
        className={cn(
          "block w-full object-cover",
          compact ? "h-full" : "h-auto max-h-[480px]",
        )}
        style={
          compact ? undefined : { aspectRatio: image.aspect ?? UNKNOWN_ASPECT }
        }
        data-testid="image-card-image"
        draggable={false}
        loading="lazy"
        onLoad={(event) =>
          rememberImageSize(
            image.url,
            event.currentTarget.naturalWidth,
            event.currentTarget.naturalHeight,
          )
        }
      />
    );
  }
  return (
    <TintBlock
      seed={frame.frameId}
      className={cn(
        "flex flex-col items-center justify-center gap-[0.6em] px-[1.2em] text-center",
        compact ? "h-full" : "max-h-[480px]",
      )}
      style={
        compact ? undefined : { aspectRatio: image?.aspect ?? DEFAULT_ASPECT }
      }
      data-testid="image-card-placeholder"
    >
      <Icons.Image
        className="h-[2.1em] w-[2.1em] opacity-60"
        aria-hidden="true"
      />
      <span className="line-clamp-2 text-[0.86em] opacity-75">{label}</span>
    </TintBlock>
  );
}
